import { inject, Injectable, Type } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscribable } from '../../../../../../../models/base/subscribable';
import { ToastService } from '../../../../../../../services/toast-service';
import { EmployersAPI } from '../../../../../../../api/employers-api';
import { DesignatedUnitFormGroup } from '../../../../../../employers/add-employer/forms/designated-unit-form-group';
import { CreateDesignatedUnitFormFactory } from '../utils/create-designated-unit-form-factory';
import { IpCreateDesignatedUnitRequest } from '../../../../../../../models/employers/plans/ip-create-designated-unit-request';
import { EmployerPlanDesignatedUnitVersion } from '../../../../../../../models/employers/plans/employer-plan-designated-unit-version';
import { EmployerPlanDesignatedUnit } from '../../../../../../../models/employers/plans/employer-plan-designated-unit';
import { CreateDesignatedUnitGeneralDetailsStepComponent } from '../step-components/create-designated-unit-general-details-step/create-designated-unit-general-details-step.component';
import { CreateDesignatedUnitContributionDetailsStepComponent } from '../step-components/create-designated-unit-contribution-details-step/create-designated-unit-contribution-details-step.component';

enum CreateDesignatedUnitStep {
  GeneralDetails,
  ContributionDetails
}

@Injectable()
export class CreateDesignatedUnitModalService extends Subscribable {
  private employersAPI = inject(EmployersAPI);
  private toastService = inject(ToastService);
  private ngbActiveModal = inject(NgbActiveModal);

  private stepComponents: Record<CreateDesignatedUnitStep, Type<any>> = {
    [CreateDesignatedUnitStep.GeneralDetails]: CreateDesignatedUnitGeneralDetailsStepComponent,
    [CreateDesignatedUnitStep.ContributionDetails]: CreateDesignatedUnitContributionDetailsStepComponent
  };

  private _currentStep = new BehaviorSubject(CreateDesignatedUnitStep.GeneralDetails);
  public currentStep$ = this._currentStep as Observable<CreateDesignatedUnitStep>;
  public currentStepComponent$ = this.currentStep$.pipe(map(currentStep => this.stepComponents[currentStep]));

  private _isLoading = new BehaviorSubject(false);
  public isLoading$ = this._isLoading as Observable<boolean>;

  private _existingDesignatedUnits = new BehaviorSubject<EmployerPlanDesignatedUnit[] | null>(null);
  public existingDesignatedUnits$ = this._existingDesignatedUnits.exists();

  private _employerPlanId = new BehaviorSubject<number | null>(null);
  public employerPlanId$ = this._employerPlanId.exists();

  public designatedUnitForm!: DesignatedUnitFormGroup;

  private listenToInitForm = combineLatest([this.existingDesignatedUnits$, this.employerPlanId$]).once(([units]) => {
    const existingUnits = units ?? [];
    this.designatedUnitForm = CreateDesignatedUnitFormFactory.createDesignatedUnitFormGroup(
      existingUnits.map(unit => unit.unitName),
      existingUnits.map(unit => unit.clientUnitId)
    );
  });

  public setExistingDesignatedUnits(units: EmployerPlanDesignatedUnit[]): void {
    this._existingDesignatedUnits.next(units);
  }

  public setEmployerPlanId(id: number): void {
    this._employerPlanId.next(id);
  }

  public goForward(): void {
    this.currentStep$.pipe(take(1)).subscribe(step => {
      this._currentStep.next(step + 1);
    });
  }

  public goBack(): void {
    this.currentStep$.pipe(take(1)).subscribe(step => {
      this._currentStep.next(step - 1);
    });
  }

  public submitDesignatedUnit(): void {
    this._isLoading.next(true);
    this.employerPlanId$
      .notNull()
      .pipe(
        take(1),
        map(employerPlanId => this.buildRequest(employerPlanId ?? -1)),
        switchMap(request => this.employersAPI.ipCreateDesignatedUnit(request.employerPlanId, request))
      )
      .subscribe({
        next: result => {
          this._isLoading.next(false);
          this.toastService.publishSuccessMessage($localize`Designated Unit Added`);
          this.ngbActiveModal.close(result);
        },
        error: err => {
          this._isLoading.next(false);
          this.toastService.publishError(err);
        }
      });
  }

  private buildRequest(employerPlanId: number): IpCreateDesignatedUnitRequest {
    const form = this.designatedUnitForm;
    const generalDetails = form.controls.generalDetails.getRawValue();
    const contributionDetails = form.controls.contributionDetails.getRawValue();

    const request = new IpCreateDesignatedUnitRequest();
    request.employerPlanId = employerPlanId;
    request.unitName = generalDetails.unitName ?? '';
    request.clientUnitId = generalDetails.clientUnitId ?? '';
    request.designatedUnitTypeId = generalDetails.designatedUnitTypeId ?? 0;
    request.remittanceFrequencyTypeId = generalDetails.remittanceFrequencyTypeId ?? 0;

    const version = new EmployerPlanDesignatedUnitVersion();
    version.startDate = generalDetails.startDate ?? '';
    version.endDate = generalDetails.endDate ?? null;
    version.contributionRateBasisTypeId = contributionDetails.contributionRateBasisTypeId ?? 0;
    version.matchedContributionRateTypeId = contributionDetails.matchedContributionRateTypeId ?? 0;
    version.matchedContributionRatePercentage = contributionDetails.matchedContributionRatePercentage;
    version.matchedContributionRateRangeFirstPercentage =
      contributionDetails.matchedContributionRateRangeFirstPercentage;
    version.matchedContributionRateRangeSecondPercentage =
      contributionDetails.matchedContributionRateRangeSecondPercentage;
    version.unmatchedContributionRateTypeId = contributionDetails.unmatchedContributionRateTypeId ?? 0;
    version.unmatchedContributionRatePercentage = contributionDetails.unmatchedContributionRatePercentage;
    version.unmatchedContributionRateRangeFirstPercentage =
      contributionDetails.unmatchedContributionRateRangeFirstPercentage;
    version.unmatchedContributionRateRangeSecondPercentage =
      contributionDetails.unmatchedContributionRateRangeSecondPercentage;
    version.fullTimeWaitingPeriod = contributionDetails.fullTimeWaitingPeriod ?? 0;
    version.lessThanFullTimeParticipationTypeId = contributionDetails.lessThanFullTimeParticipationTypeId;
    version.lessThanFullTimeWaitingPeriod = contributionDetails.lessThanFullTimeWaitingPeriod ?? 0;
    request.version = version;

    return request;
  }
}
